import type { FileInfo } from "@exagens/behavioral-insight-console-client";
import { SparxTab } from "@/features/sparxsEditor/components/organisms/tab/editorTabsSelector";

export const EDITOR_SEARCH_QUERY_PARAM = "search" as const;

/** Named panel path segments for `/sparxseditor/:id/:panel`. */
export const EDITOR_PANEL_SLUGS = {
  TEASER: "teaser",
  CONTENT: "content",
  ADVISOR: "advisor",
  METADATA: "metadata",
} as const;

export type EditorPanelSlug =
  (typeof EDITOR_PANEL_SLUGS)[keyof typeof EDITOR_PANEL_SLUGS];

const PANEL_SLUG_TO_TAB: Record<EditorPanelSlug, SparxTab> = {
  [EDITOR_PANEL_SLUGS.TEASER]: SparxTab.TEASER,
  [EDITOR_PANEL_SLUGS.CONTENT]: SparxTab.CONTENT,
  [EDITOR_PANEL_SLUGS.ADVISOR]: SparxTab.ADVISOR,
  [EDITOR_PANEL_SLUGS.METADATA]: SparxTab.METADATA,
};

const TAB_TO_PANEL_SLUG: Record<SparxTab, EditorPanelSlug> = {
  [SparxTab.TEASER]: EDITOR_PANEL_SLUGS.TEASER,
  [SparxTab.CONTENT]: EDITOR_PANEL_SLUGS.CONTENT,
  [SparxTab.ADVISOR]: EDITOR_PANEL_SLUGS.ADVISOR,
  [SparxTab.METADATA]: EDITOR_PANEL_SLUGS.METADATA,
};

export const DEFAULT_EDITOR_PANEL_SLUG = EDITOR_PANEL_SLUGS.TEASER;

export const isEditorPanelSlug = (value: string): value is EditorPanelSlug =>
  (Object.values(EDITOR_PANEL_SLUGS) as string[]).includes(value);

export const editorPanelSlugFromUnknown = (
  slug: string | undefined,
): EditorPanelSlug | undefined => {
  if (slug == null || slug.length === 0) {
    return undefined;
  }
  const normalized = slug.toLowerCase();
  return isEditorPanelSlug(normalized) ? normalized : undefined;
};

export const panelSlugFromSparxTab = (tab: SparxTab): EditorPanelSlug =>
  TAB_TO_PANEL_SLUG[tab];

export const sparxTabFromPanelSlug = (
  slug: string | undefined,
): SparxTab | undefined => {
  const panelSlug = editorPanelSlugFromUnknown(slug);
  if (panelSlug == null) {
    return undefined;
  }
  return PANEL_SLUG_TO_TAB[panelSlug];
};

export const catalogPanelSlugFromRoute = (
  panel: string | undefined,
): EditorPanelSlug => {
  const tab = sparxTabFromPanelSlug(panel);
  return tab == null ? DEFAULT_EDITOR_PANEL_SLUG : panelSlugFromSparxTab(tab);
};

export const sparxsEditorPathPartsFromPathname = (
  pathname: string,
): { id: string | undefined; panel: string | undefined } => {
  const parts = pathname.split("/").filter((segment) => segment.length > 0);
  if (parts[0] !== "sparxseditor") {
    return { id: undefined, panel: undefined };
  }
  const idSegment = parts[1];
  const panelSegment = parts[2];
  return {
    id:
      idSegment != null && idSegment.length > 0
        ? decodeURIComponent(idSegment)
        : undefined,
    panel:
      panelSegment != null && panelSegment.length > 0
        ? panelSegment
        : undefined,
  };
};

export const buildSparxsEditorPath = (
  fileName: string,
  panelSlug: EditorPanelSlug,
): string => `/sparxseditor/${encodeURIComponent(fileName)}/${panelSlug}`;

export const resolveDefaultEditorFileName = (args: {
  sessionActiveFileName: string;
  filteredFirstFileName: string | undefined;
  fullCatalog: readonly FileInfo[];
}): string | undefined => {
  const fullSet = new Set(args.fullCatalog.map((f) => f.fileName));
  if (
    args.sessionActiveFileName.length > 0 &&
    fullSet.has(args.sessionActiveFileName)
  ) {
    return args.sessionActiveFileName;
  }
  if (
    args.filteredFirstFileName != null &&
    args.filteredFirstFileName.length > 0
  ) {
    return args.filteredFirstFileName;
  }
  return args.fullCatalog[0]?.fileName;
};
